export const TEAM_NAME = "Ν.Α.Ο. Σούδας";

// Volleyball set score rule: sets 1-4 to 25, set 5 to 15, win by exactly 2 when past the target.
export function setScoreError(setNumber, us, them, t) {
  const target = Number(setNumber) === 5 ? 15 : 25;
  const hi = Math.max(us, them);
  const lo = Math.min(us, them);
  if (hi < target) return t("dv_err_min_points").replace("{n}", target);
  if (hi - lo < 2) return t("dv_err_diff");
  if (hi > target && hi - lo !== 2) return t("dv_err_exact_two").replace("{n}", target);
  return null;
}

// Returns {us, them, decided, decidedAt} — decidedAt = set number that clinched the match (or null)
export function matchResult(setScores) {
  let us = 0;
  let them = 0;
  let decidedAt = null;
  for (let n = 1; n <= 5; n += 1) {
    const sc = setScores?.[String(n)];
    if (!sc || decidedAt) continue;
    if (sc[0] > sc[1]) us += 1;
    else if (sc[1] > sc[0]) them += 1;
    if (us === 3 || them === 3) decidedAt = n;
  }
  return { us, them, decided: decidedAt !== null, decidedAt };
}

export const POSITIONS = ["Ακραίος", "Διαγώνιος", "Κεντρικός", "Πασαδόρος", "Λίμπερο"];
const POSITIONS_EN = { "Ακραίος": "Outside hitter", "Διαγώνιος": "Opposite", "Κεντρικός": "Middle blocker", "Πασαδόρος": "Setter", "Λίμπερο": "Libero" };
export const positionLabel = (pos, lang) => (lang === "en" ? POSITIONS_EN[pos] || pos : pos);

export const GROUPS = [
  {
    category: "reception",
    labelKey: "dv_reception",
    types: [
      { type: "excellent", labelKey: "dv_excellent" },
      { type: "medium", labelKey: "dv_medium" },
      { type: "poor", labelKey: "dv_poor" },
    ],
    header: "bg-emerald-500 text-white",
    band: "bg-emerald-50",
    minus: "bg-emerald-200/80 text-emerald-900 hover:bg-emerald-300",
    plus: "bg-emerald-500 text-white hover:bg-emerald-600 shadow-[0_4px_0_0_#047857]",
  },
  {
    category: "attack",
    labelKey: "dv_attack",
    types: [
      { type: "point", labelKey: "dv_point" },
      { type: "inplay", labelKey: "dv_inplay" },
      { type: "error", labelKey: "dv_error" },
    ],
    header: "bg-rose-500 text-white",
    band: "bg-rose-50",
    minus: "bg-rose-200/80 text-rose-900 hover:bg-rose-300",
    plus: "bg-rose-500 text-white hover:bg-rose-600 shadow-[0_4px_0_0_#be123c]",
  },
  {
    category: "serve",
    labelKey: "dv_serve",
    types: [
      { type: "ace", labelKey: "dv_ace" },
      { type: "inplay", labelKey: "dv_passed" },
      { type: "error", labelKey: "dv_missed" },
    ],
    header: "bg-sky-600 text-white",
    band: "bg-sky-50",
    minus: "bg-sky-200/80 text-sky-900 hover:bg-sky-300",
    plus: "bg-sky-600 text-white hover:bg-sky-700 shadow-[0_4px_0_0_#075985]",
  },
  {
    category: "block",
    labelKey: "dv_block",
    types: [{ type: "point", labelKey: "dv_point" }],
    header: "bg-amber-400 text-slate-900",
    band: "bg-amber-50",
    minus: "bg-amber-200/80 text-amber-900 hover:bg-amber-300",
    plus: "bg-amber-400 text-slate-900 hover:bg-amber-500 shadow-[0_4px_0_0_#b45309]",
  },
];

// Setter-only group, shown instead of reception for Πασαδόρος (same green styling)
export const SET_GROUP = {
  ...GROUPS[0],
  category: "set",
  labelKey: "dv_set",
  types: [
    { type: "excellent", labelKey: "dv_excellent" },
    { type: "inplay", labelKey: "dv_inplay" },
    { type: "error", labelKey: "dv_error" },
  ],
};
export const isSetter = (p) => p.position === "Πασαδόρος";
export const sortRoster = (roster) => [...roster].sort((a, b) => Number(isSetter(a)) - Number(isSetter(b)) || a.number - b.number);
export const groupsFor = (p) => (isSetter(p) ? [SET_GROUP, ...GROUPS.slice(1)] : GROUPS);

export const STAT_KEYS = [...GROUPS, SET_GROUP].flatMap((g) => g.types.map((t) => `${g.category}_${t.type}`));

// Setters: legacy reception / "good" set events are counted as ΠΑΣΑ
const SETTER_REMAP = {
  reception_excellent: "set_excellent",
  reception_medium: "set_inplay",
  reception_poor: "set_error",
  set_good: "set_excellent",
};
export const statKeyFor = (player, key) => (player && isSetter(player) && SETTER_REMAP[key]) || key;

export const emptyCounts = () => Object.fromEntries(STAT_KEYS.map((k) => [k, 0]));

export const pct = (num, den) => (den > 0 ? Math.round((num * 100) / den) : 0);

export function summarize(c) {
  const receptionTotal = c.reception_excellent + c.reception_medium + c.reception_poor;
  const attackTotal = c.attack_point + c.attack_inplay + c.attack_error;
  const serveTotal = c.serve_ace + c.serve_inplay + c.serve_error;
  const setTotal = c.set_excellent + c.set_inplay + c.set_error;
  return {
    ...c,
    receptionTotal,
    receptionPct: pct(c.reception_excellent, receptionTotal),
    attackTotal,
    attackPct: pct(c.attack_point, attackTotal),
    serveTotal,
    aces: c.serve_ace,
    blocks: c.block_point,
    setTotal,
    setPct: pct(c.set_excellent, setTotal),
    errorsTotal: c.reception_poor + c.attack_error + c.serve_error + c.set_error,
  };
}

// setNumber: 1..5 or null for whole match. Returns { players: {pid: summary}, team: summary }
export function computeStats(roster, events, setNumber) {
  const byId = Object.fromEntries(roster.map((p) => [p.player_id, p]));
  const counts = Object.fromEntries(roster.map((p) => [p.player_id, emptyCounts()]));
  for (const e of events) {
    if (setNumber && e.set_number !== setNumber) continue;
    const key = statKeyFor(byId[e.player_id], `${e.category}_${e.type}`);
    if (!counts[e.player_id] || !(key in counts[e.player_id])) continue;
    counts[e.player_id][key] += e.delta ?? 1;
  }
  const team = emptyCounts();
  const players = {};
  for (const pid of Object.keys(counts)) {
    for (const k of STAT_KEYS) {
      counts[pid][k] = Math.max(0, counts[pid][k]);
      team[k] += counts[pid][k];
    }
    players[pid] = summarize(counts[pid]);
  }
  return { players, team: summarize(team) };
}
